import React, {
  memo,
  forwardRef,
  useRef,
  useEffect,
  useImperativeHandle,
} from "react";
import { Container } from "@pixi/react";
// import * as PIXI from "pixi.js";
// import anime from "animejs";
import HoldAndWinOverlayCell from "./HoldAndWinOverlayCell";
import { HOLD_AND_WIN_COLUMNS, HOLD_AND_WIN_ROWS } from "./HoldAndWinBoard";
// import { toDataRow } from "./chestCollection";

// ── The locked rows (GDD "Locked-Row Symbols": "Show dormant symbols through
// a locked overlay until activation") ──
// NOW the room's locked_reel plates (HoldAndWinGameViewController
// .attachLockedRows, 2026-09-30) — the bars below are commented out.
// A black bar over each locked row — a stand-in until the animator's lock art
// fills the room's attachment_locked_reel_1..4 slots (Vladimir, 2026-09-29:
// "a black overlay with like 0.75 opacity").
// const LOCKED_ALPHA = 0.75;
// Its fade when the row opens. 250 (the win dim's undim) was over before it
// could be seen (Vladimir, 2026-09-29).
// const UNLOCK_FADE_MS = 250;
// const UNLOCK_FADE_MS = 500;
// Back to short — it no longer holds the round (non-blocking, with the coin
// counter): "short fade and done" (Vladimir, 2026-09-29).
// const UNLOCK_FADE_MS = 250;

/**
 * HoldAndWinOverlay — the live spines over the Hold & Win board.
 *
 * Mounted in the room's FRONT slot, laid out exactly like HoldAndWinBoard
 * (same cells, same centring), so a spine sits on its cell. Above the frame
 * and outside the window clip, so a landing's flames can reach past the cell.
 *
 * Imperative API (ref): play(col, row, symbolId, value), clear(col, row),
 * clearAll(), collect(col, row, value, instant), setValue(col, row, value),
 * cellAnchor(col, row) (the cell's centre, for trails), trailLayer() (above
 * every cell), cellHeight(). Cells are
 * [column][row], row 0 = the top screen row.
 */
const HoldAndWinOverlay = forwardRef(({ cellWidth, cellHeight }, ref) => {
  const containerRefs = useRef(
    Array.from({ length: HOLD_AND_WIN_COLUMNS }, () =>
      Array.from({ length: HOLD_AND_WIN_ROWS }, () => React.createRef()),
    ),
  );
  const cells = useRef(null);
  // The chest collections' fire trails fly here — above every cell.
  const trailLayerRef = useRef(null);
  // One black bar per screen row, above the cells (locked rows only show).
  // const lockLayerRef = useRef(null);
  // const lockBars = useRef([]);
  // const unlockedRef = useRef(4);

  // One overlay cell per board cell, built once.
  useEffect(() => {
    cells.current = containerRefs.current.map((column) =>
      column.map((r) => (r.current ? new HoldAndWinOverlayCell(r.current) : null)),
    );
    return () => {
      cells.current?.forEach((column) => column.forEach((c) => c?.destroy()));
      cells.current = null;
    };
  }, []);

  useEffect(() => {
    cells.current?.forEach((column) =>
      column.forEach((c) => c?.setCellHeight(cellHeight)),
    );
  }, [cellHeight]);

  // The lock bars — built once, redrawn to the cell size.
  // useEffect(() => {
    // const layer = lockLayerRef.current;
    // if (!layer) return undefined;
    // if (!lockBars.current.length) {
      // lockBars.current = Array.from({ length: HOLD_AND_WIN_ROWS }, (_, screenRow) => {
        // const bar = new PIXI.Graphics();
        // // Built already showing the rows last set (setUnlockedRows may have
        // // run before the bars existed — they would have stayed invisible).
        // bar.alpha = toDataRow(screenRow) >= unlockedRef.current ? LOCKED_ALPHA : 0;
        // bar.eventMode = "none";
        // layer.addChild(bar);
        // return bar;
      // });
    // }
    // const width = HOLD_AND_WIN_COLUMNS * cellWidth;
    // const x0 = -width / 2;
    // const y0 = -(HOLD_AND_WIN_ROWS / 2) * cellHeight;
    // lockBars.current.forEach((bar, screenRow) => {
      // bar.clear();
      // bar.beginFill(0x000000, 1);
      // bar.drawRect(x0, y0 + screenRow * cellHeight, width, cellHeight);
      // bar.endFill();
    // });
    // return undefined;
  // }, [cellWidth, cellHeight]);

  // useEffect(
    // () => () => {
      // lockBars.current.forEach((bar) => {
        // anime.remove(bar);
        // // The lock layer's own unmount has usually destroyed its children
        // // already — a second destroy() throws (null geometry) and took the
        // // whole tree down on the room's exit.
        // if (!bar.destroyed) bar.destroy();
      // });
      // lockBars.current = [];
    // },
    // [],
  // );

  useImperativeHandle(
    ref,
    () => ({
      play: (col, row, symbolId, value, skin = null) => {
        const cell = cells.current?.[col]?.[row];
        if (!cell) return false;
        cell.setCellHeight(cellHeight);
        return cell.play(symbolId, value, skin);
      },
      clear: (col, row) => cells.current?.[col]?.[row]?.clear(),
      clearAll: () =>
        cells.current?.forEach((column) => column.forEach((c) => c?.clear())),
      collect: (col, row, value, instant = false) =>
        cells.current?.[col]?.[row]?.collect(value, instant) ?? Promise.resolve(),
      // The Scarab reveal (scarabReveal.js) — see the cell's methods.
      rig: (col, row) => cells.current?.[col]?.[row]?.rig?.() ?? null,
      playWin: (col, row) =>
        cells.current?.[col]?.[row]?.playWin?.() ?? Promise.resolve(),
      revealScarab: (col, row, multiplier) =>
        cells.current?.[col]?.[row]?.revealScarab?.(multiplier) ?? Promise.resolve(),
      showScarabMultiplier: (col, row, multiplier) =>
        cells.current?.[col]?.[row]?.showMultiplier?.(multiplier),
      revealJackpotValue: (col, row, value) =>
        cells.current?.[col]?.[row]?.revealJackpotValue?.(value) ?? Promise.resolve(),
      // One full pass of the cell's idle loop (cell.idleLoopEnd).
      idleLoopEnd: (col, row) =>
        cells.current?.[col]?.[row]?.idleLoopEnd?.() ?? Promise.resolve(),
      cellAnchor: (col, row) => containerRefs.current[col]?.[row]?.current ?? null,
      setValue: (col, row, value) => cells.current?.[col]?.[row]?.setValue(value),
      darken: (col, row) => cells.current?.[col]?.[row]?.darken(),
      valueLabel: (col, row) => cells.current?.[col]?.[row]?.valueLabel() ?? null,
      // Resolves when the cell's landing clips have played out (cell.landed).
      landed: (col, row) => cells.current?.[col]?.[row]?.landed?.() ?? Promise.resolve(),
      trailLayer: () => trailLayerRef.current,
      // ── Locked rows ──
      // The rows past `unlockedRows` (data rows) under their bar, at once —
      // the room's first frame, a reload.
      // setUnlockedRows: (unlockedRows) => {
        // unlockedRef.current = unlockedRows;
        // lockBars.current.forEach((bar, screenRow) => {
          // anime.remove(bar);
          // bar.alpha = toDataRow(screenRow) >= unlockedRows ? LOCKED_ALPHA : 0;
        // });
      // },
      // // The rows from `from` to `to` (data rows) open: their bars fade away.
      // // Resolves when the fade is done.
      // unlockRows: (from, to) => {
        // unlockedRef.current = to;
        // const opening = lockBars.current.filter((_, screenRow) => {
          // const d = toDataRow(screenRow);
          // return d >= from && d < to;
        // });
        // if (!opening.length) return Promise.resolve();
        // return new Promise((resolve) => {
          // opening.forEach((bar) => anime.remove(bar));
          // anime({
            // targets: opening,
            // alpha: 0,
            // duration: UNLOCK_FADE_MS,
            // easing: "easeInQuad",
            // complete: () => resolve(),
          // });
        // });
      // },
      cellHeight: () => cellHeight,
    }),
    [cellHeight],
  );

  const left = -((HOLD_AND_WIN_COLUMNS - 1) / 2) * cellWidth;
  const top = -((HOLD_AND_WIN_ROWS - 1) / 2) * cellHeight;

  return (
    <Container name="hold_and_win_overlay" sortableChildren>
      {Array.from({ length: HOLD_AND_WIN_COLUMNS }, (_, c) =>
        Array.from({ length: HOLD_AND_WIN_ROWS }, (_, r) => (
          <Container
            key={`hnw_ov_${c}_${r}`}
            ref={containerRefs.current[c][r]}
            x={left + c * cellWidth}
            y={top + r * cellHeight}
          />
        )),
      )}
      {/* Above every cell, below the trails. */}
      {/* The black lock bars — replaced by the room's locked_reel plates
          (HoldAndWinGameViewController.attachLockedRows, 2026-09-30).
      <Container
        ref={lockLayerRef}
        name="hold_and_win_lock_layer"
        zIndex={500}
      />
      */}
      <Container
        ref={trailLayerRef}
        name="hold_and_win_trail_layer"
        zIndex={1000}
        sortableChildren
      />
    </Container>
  );
});

HoldAndWinOverlay.displayName = "HoldAndWinOverlay";

export default memo(HoldAndWinOverlay);
